import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";

const marquee = (distance) => keyframes`
  from { transform: translate3d(0,0,0); }
  to   { transform: translate3d(-${distance}px, 0, 0); }
`;

export default function IconDividerLine({
                                            iconSrc,
                                            iconNode,

                                            iconSize = { xs: 30, sm: 30, md: 30, lg: 30, xl: 30 },
                                            iconGap = 0.5,
                                            rotateIcon = false,

                                            // motion controls (same idea as SMPhrasesScroller)
                                            pixelsPerSecond = 60,
                                            pauseOnHover = false,

                                            containerSx,
                                            lineSx,
                                        }) {
    const theme = useTheme();

    const viewportRef = useRef(null);
    const measureRef = useRef(null);

    const [sequenceWidth, setSequenceWidth] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);

    // Resolve responsive iconSize to a number (for counting how many icons to render in one sequence)
    const upSm = useMediaQuery(theme.breakpoints.up("sm"));
    const upMd = useMediaQuery(theme.breakpoints.up("md"));
    const upLg = useMediaQuery(theme.breakpoints.up("lg"));
    const upXl = useMediaQuery(theme.breakpoints.up("xl"));

    const resolvedIconSize = useMemo(() => {
        if (typeof iconSize === "number") return iconSize;

        if (iconSize && typeof iconSize === "object") {
            if (upXl && iconSize.xl != null) return Number(iconSize.xl);
            if (upLg && iconSize.lg != null) return Number(iconSize.lg);
            if (upMd && iconSize.md != null) return Number(iconSize.md);
            if (upSm && iconSize.sm != null) return Number(iconSize.sm);
            if (iconSize.xs != null) return Number(iconSize.xs);
        }

        const n = parseFloat(String(iconSize));
        return Number.isFinite(n) ? n : 18;
    }, [iconSize, upSm, upMd, upLg, upXl]);

    const rotationValue =
        rotateIcon === true ? 90 : typeof rotateIcon === "number" ? rotateIcon : 0;

    // Build ONE sequence: enough icons to span the viewport (plus buffer)
    const iconCountInSequence = useMemo(() => {
        const spacingPx = parseFloat(theme.spacing(1)); // usually 8
        const gapPx = (Number(iconGap) || 0) * spacingPx;
        const perIcon = resolvedIconSize + gapPx;

        if (!viewportWidth || perIcon <= 0) return 12;
        return Math.max(6, Math.ceil(viewportWidth / perIcon) + 4);
    }, [theme, iconGap, resolvedIconSize, viewportWidth]);

    useEffect(() => {
        if (!viewportRef.current || !measureRef.current) return;

        const update = () => {
            const vw = viewportRef.current?.getBoundingClientRect()?.width || 0;
            const sw = measureRef.current?.getBoundingClientRect()?.width || 0;
            setViewportWidth(vw);
            setSequenceWidth(sw);
        };

        update();

        const ro = new ResizeObserver(update);
        ro.observe(viewportRef.current);
        ro.observe(measureRef.current);

        return () => ro.disconnect();
    }, [iconCountInSequence]);

    if (!iconSrc && !iconNode) return null;

    // Repeat enough times so there is never a blank gap.
    const repeats =
        sequenceWidth > 0 && viewportWidth > 0
            ? Math.max(2, Math.ceil(viewportWidth / sequenceWidth) + 2)
            : 3;

    // Duration is based on how far we travel (one full sequence width)
    const durationSeconds =
        sequenceWidth > 0
            ? Math.max(6, sequenceWidth / Math.max(10, pixelsPerSecond))
            : 20;

    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                marginTop: { xs: -1, sm: -2, md: -2.5, lg: -3, xl: -3 },
                paddingBottom: 1,
                ...containerSx,
            }}
        >
            {/* Viewport */}
            <Box
                ref={viewportRef}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    ...lineSx,
                }}
            >
                {/* Track */}
                <Box
                    sx={{
                        display: "flex",
                        width: "max-content",
                        willChange: "transform",
                        animation:
                            sequenceWidth > 0
                                ? `${marquee(sequenceWidth)} ${durationSeconds}s linear infinite`
                                : "none",
                        ...(pauseOnHover && { "&:hover": { animationPlayState: "paused" } }),
                    }}
                >
                    {/* Hidden measurer: EXACT same row styling, not visible */}
                    <Box
                        ref={measureRef}
                        sx={{
                            position: "absolute",
                            visibility: "hidden",
                            pointerEvents: "none",
                            height: 0,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <IconRow
                            count={iconCountInSequence}
                            iconSrc={iconSrc}
                            iconNode={iconNode}
                            iconSize={iconSize}
                            iconGap={iconGap}
                            rotationValue={rotationValue}
                        />
                    </Box>

                    {/* Repeated sequences */}
                    {Array.from({ length: repeats }).map((_, idx) => (
                        <IconRow
                            key={`row-${idx}`}
                            count={iconCountInSequence}
                            iconSrc={iconSrc}
                            iconNode={iconNode}
                            iconSize={iconSize}
                            iconGap={iconGap}
                            rotationValue={rotationValue}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

function IconRow({
                     count,
                     iconSrc,
                     iconNode,
                     iconSize,
                     iconGap,
                     rotationValue,
                 }) {
    const Icon = ({ index }) => {
        if (iconNode) {
            return (
                <Box
                    key={index}
                    sx={{
                        display: "inline-flex",
                        flex: "0 0 auto",
                        transform: `rotate(${rotationValue}deg)`,
                        transformOrigin: "center",
                    }}
                >
                    {iconNode}
                </Box>
            );
        }

        return (
            <Box
                key={index}
                component="img"
                src={iconSrc}
                alt=""
                sx={{
                    width: iconSize,
                    height: iconSize,
                    display: "block",
                    objectFit: "contain",
                    flex: "0 0 auto",
                    transform: `rotate(${rotationValue}deg)`,
                    transformOrigin: "center",
                }}
            />
        );
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                columnGap: (theme) => theme.spacing(iconGap),
                whiteSpace: "nowrap",
            }}
        >
            {Array.from({ length: count }).map((_, i) => (
                <Icon key={i} index={i} />
            ))}
        </Box>
    );
}
